import React, { useEffect, useState, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import { toast } from 'sonner';
import { User, FileText, Upload, LogOut, CheckCircle2, Clock, XCircle, AlertCircle, Loader2, CreditCard, Download } from 'lucide-react';
import { useMemberAuth } from '../../context/MemberAuthContext';
import memberApi from '../../lib/memberApi';
import { getFileUrl } from '../../lib/api';
import PaymentsPanel from './PaymentsPanel';

const inputCls =
  'w-full px-3 py-2.5 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--gold))]';

const STATUS_MAP = {
  pending: { label: 'İnceleniyor', cls: 'bg-amber-500/10 text-amber-600 dark:text-amber-400', icon: Clock },
  approved: { label: 'Onaylandı', cls: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400', icon: CheckCircle2 },
  rejected: { label: 'Reddedildi', cls: 'bg-destructive/10 text-destructive', icon: XCircle },
  re_requested: { label: 'Yeniden İstendi', cls: 'bg-destructive/10 text-destructive', icon: AlertCircle },
};

export default function MemberProfile() {
  const { isAuthenticated, loading: authLoading, member, logout, setMember } = useMemberAuth();
  const [loading, setLoading] = useState(true);
  const [fields, setFields] = useState([]);
  const [basic, setBasic] = useState({ first_name: '', last_name: '', phone: '', email: '' });
  const [profileData, setProfileData] = useState({});
  const [completion, setCompletion] = useState(0);
  const [documentsEnabled, setDocumentsEnabled] = useState(false);
  const [paymentsEnabled, setPaymentsEnabled] = useState(false);
  const [wetSigEnabled, setWetSigEnabled] = useState(false);
  const [sig, setSig] = useState({ mailing_address: '', documents: [], submission: null, warning: { enabled: false, message: '' } });
  const [shipForm, setShipForm] = useState({ shipping_company: '', tracking_note: '' });
  const [showSigWarning, setShowSigWarning] = useState(false);
  const [docs, setDocs] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadingKey, setUploadingKey] = useState(null);
  const [uploadingDoc, setUploadingDoc] = useState(null);
  const [activeTab, setActiveTab] = useState('profile');

  const loadDocs = useCallback(async () => {
    try {
      const res = await memberApi.get('/members/documents');
      setDocs(res.data);
    } catch {
      /* documents disabled */
    }
  }, []);

  const loadSig = useCallback(async () => {
    try {
      const res = await memberApi.get('/signature-documents');
      setSig(res.data);
      const sub = res.data.submission;
      setShipForm({ shipping_company: sub?.shipping_company || '', tracking_note: sub?.tracking_note || '' });
      return res.data;
    } catch {
      /* wet-signature disabled */
      return null;
    }
  }, []);

  const shipSig = async () => {
    const company = (shipForm.shipping_company || '').trim();
    if (!company) { toast.error('Lütfen kargo firmasını girin'); return; }
    try {
      await memberApi.post('/signature-ship', { shipping_company: company, tracking_note: shipForm.tracking_note || '' });
      toast.success('Kargo bilginiz alındı. Teslim sonrası onaylanacak.');
      setShowSigWarning(false);
      await loadSig();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Hata oluştu');
    }
  };

  const maybeWarn = (data, memberId) => {
    const w = data?.warning;
    if (!w?.enabled || !w?.message) return;
    const sub = data?.submission;
    if (sub?.status === 'shipped' || sub?.status === 'completed') return;
    if (!(data?.documents?.length > 0)) return;
    const key = `sigw_shown_${memberId}`;
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, '1');
    setShowSigWarning(true);
  };

  useEffect(() => {
    if (!isAuthenticated) return;
    (async () => {
      try {
        const [meRes, fieldsRes, statusRes, payCfg] = await Promise.all([
          memberApi.get('/members/me'),
          memberApi.get('/members/fields'),
          memberApi.get('/membership/status'),
          memberApi.get('/payments/config').catch(() => ({ data: { payments_enabled: false } })),
        ]);
        const me = meRes.data;
        setBasic({ first_name: me.first_name || '', last_name: me.last_name || '', phone: me.phone || '', email: me.email || '' });
        setProfileData(me.profile_data || {});
        setCompletion(me.profile_completion || 0);
        setMember(me);
        setFields(fieldsRes.data);
        setDocumentsEnabled(!!statusRes.data.documents_enabled);
        setPaymentsEnabled(!!payCfg.data.payments_enabled);
        setWetSigEnabled(!!statusRes.data.wet_signature_enabled);
        if (statusRes.data.documents_enabled) await loadDocs();
        if (statusRes.data.wet_signature_enabled) {
          const sd = await loadSig();
          maybeWarn(sd, me.id);
        }
      } catch {
        toast.error('Profil bilgileri yüklenemedi');
      } finally {
        setLoading(false);
      }
    })();
  }, [isAuthenticated, loadDocs, setMember]);

  if (authLoading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[hsl(var(--gold))] animate-spin" />
      </div>
    );
  }
  if (!isAuthenticated) return <Navigate to="/uye/giris" replace />;

  const setField = (key, val) => setProfileData((p) => ({ ...p, [key]: val }));

  const handleFieldImage = async (key, file) => {
    if (!file) return;
    setUploadingKey(key);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await memberApi.post('/members/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setField(key, res.data.path);
      toast.success('Görsel yüklendi');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Yükleme başarısız');
    } finally {
      setUploadingKey(null);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const res = await memberApi.put('/members/profile', {
        first_name: basic.first_name,
        last_name: basic.last_name,
        phone: basic.phone,
        profile_data: profileData,
      });
      setCompletion(res.data.profile_completion ?? completion);
      toast.success('Profiliniz güncellendi');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Güncelleme başarısız');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDocUpload = async (definitionId, file) => {
    if (!file) return;
    setUploadingDoc(definitionId);
    try {
      const fd = new FormData();
      fd.append('definition_id', definitionId);
      fd.append('file', file);
      await memberApi.post('/members/documents', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Belge yüklendi, inceleme bekleniyor');
      await loadDocs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Belge yüklenemedi');
    } finally {
      setUploadingDoc(null);
    }
  };

  const renderField = (f) => {
    const val = profileData[f.field_key] ?? '';
    if (f.field_type === 'image') {
      return (
        <div className="flex items-center gap-4">
          <div className="w-20 h-20 rounded-xl border border-border bg-secondary overflow-hidden flex items-center justify-center shrink-0">
            {val ? <img src={getFileUrl(val)} alt={f.label} className="w-full h-full object-cover" /> : <User className="w-7 h-7 text-muted-foreground" />}
          </div>
          <label className="cursor-pointer">
            <span className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-border text-sm font-medium hover:bg-secondary transition-colors">
              {uploadingKey === f.field_key ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              Görsel Seç
            </span>
            <input type="file" accept="image/*" className="hidden" onChange={(e) => handleFieldImage(f.field_key, e.target.files[0])} data-testid={`field-${f.field_key}`} />
          </label>
        </div>
      );
    }
    if (f.field_type === 'textarea') {
      return <textarea value={val} onChange={(e) => setField(f.field_key, e.target.value)} rows={3} className={inputCls} data-testid={`field-${f.field_key}`} />;
    }
    if (f.field_type === 'select') {
      return (
        <select value={val} onChange={(e) => setField(f.field_key, e.target.value)} className={inputCls} data-testid={`field-${f.field_key}`}>
          <option value="">Seçiniz</option>
          {(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      );
    }
    return <input type={f.field_type === 'date' ? 'date' : 'text'} value={val} onChange={(e) => setField(f.field_key, e.target.value)} className={inputCls} data-testid={`field-${f.field_key}`} />;
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10" data-testid="member-profile-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-heading text-2xl font-semibold">Merhaba, {member?.first_name || basic.first_name}</h1>
          <p className="text-sm text-muted-foreground mt-0.5">{basic.email}</p>
        </div>
        <button onClick={logout} className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-border text-sm font-medium text-destructive hover:bg-destructive/10 transition-colors self-start" data-testid="member-logout">
          <LogOut className="w-4 h-4" /> Çıkış Yap
        </button>
      </div>

      {/* Completion */}
      <div className="mb-6 p-4 rounded-xl bg-card border border-border">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium">Profil Tamamlanma</span>
          <span className="text-sm font-semibold text-[hsl(var(--gold))]" data-testid="profile-completion">%{completion}</span>
        </div>
        <div className="h-2 rounded-full bg-secondary overflow-hidden">
          <div className="h-full bg-[hsl(var(--gold))] rounded-full transition-[width] duration-300" style={{ width: `${completion}%` }} />
        </div>
      </div>

      {loading ? (
        <div className="min-h-[30vh] flex items-center justify-center">
          <Loader2 className="w-7 h-7 text-[hsl(var(--gold))] animate-spin" />
        </div>
      ) : (
        <div className="w-full">
          <div className="mb-4 inline-flex items-center gap-1 p-1 rounded-lg bg-secondary" role="tablist">
            <button type="button" onClick={() => setActiveTab('profile')} data-testid="tab-profile"
              className={`inline-flex items-center px-4 py-2 rounded-md text-sm font-medium transition-colors duration-200 ${activeTab === 'profile' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}>
              <User className="w-4 h-4 mr-1.5" /> Profil Bilgileri
            </button>
            {documentsEnabled && (
              <button type="button" onClick={() => setActiveTab('documents')} data-testid="tab-documents"
                className={`inline-flex items-center px-4 py-2 rounded-md text-sm font-medium transition-colors duration-200 ${activeTab === 'documents' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}>
                <FileText className="w-4 h-4 mr-1.5" /> Belgelerim
              </button>
            )}
            {paymentsEnabled && (
              <button type="button" onClick={() => setActiveTab('payments')} data-testid="tab-payments"
                className={`inline-flex items-center px-4 py-2 rounded-md text-sm font-medium transition-colors duration-200 ${activeTab === 'payments' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}>
                <CreditCard className="w-4 h-4 mr-1.5" /> Ödemeler
              </button>
            )}
            {wetSigEnabled && (
              <button type="button" onClick={() => setActiveTab('signature')} data-testid="tab-signature"
                className={`inline-flex items-center px-4 py-2 rounded-md text-sm font-medium transition-colors duration-200 ${activeTab === 'signature' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}>
                <FileText className="w-4 h-4 mr-1.5" /> Islak İmza Belgeleri
              </button>
            )}
          </div>

          {/* Profile tab */}
          {activeTab === 'profile' && (
            <form onSubmit={handleSave} className="space-y-5 p-6 rounded-2xl bg-card border border-border" data-testid="profile-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1.5">Ad</label>
                  <input value={basic.first_name} onChange={(e) => setBasic({ ...basic, first_name: e.target.value })} className={inputCls} required data-testid="profile-firstname" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1.5">Soyad</label>
                  <input value={basic.last_name} onChange={(e) => setBasic({ ...basic, last_name: e.target.value })} className={inputCls} required data-testid="profile-lastname" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1.5">Telefon</label>
                  <input value={basic.phone} onChange={(e) => setBasic({ ...basic, phone: e.target.value })} className={inputCls} data-testid="profile-phone" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1.5">E-posta</label>
                  <input value={basic.email} disabled className={`${inputCls} opacity-60 cursor-not-allowed`} />
                </div>
              </div>

              {fields.length > 0 && <div className="border-t border-border pt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
                {fields.map((f) => (
                  <div key={f.field_key} className={f.field_type === 'textarea' || f.field_type === 'image' ? 'sm:col-span-2' : ''}>
                    <label className="block text-sm font-medium mb-1.5">
                      {f.label} {f.is_required && <span className="text-destructive">*</span>}
                    </label>
                    {renderField(f)}
                  </div>
                ))}
              </div>}

              <div className="flex justify-end pt-2">
                <button type="submit" disabled={isSubmitting}
                  className="px-5 py-2.5 rounded-lg bg-[hsl(var(--gold))] text-[hsl(var(--accent-foreground))] font-medium text-sm hover:bg-[hsl(var(--gold)/0.92)] active:scale-[0.98] transition-colors duration-200 disabled:opacity-50"
                  data-testid="profile-save">
                  {isSubmitting ? 'Kaydediliyor...' : 'Bilgilerimi Kaydet'}
                </button>
              </div>
            </form>
          )}

          {/* Documents tab */}
          {documentsEnabled && activeTab === 'documents' && (
            <div className="space-y-3" data-testid="documents-list">
                {docs.length === 0 && (
                  <div className="p-8 text-center text-sm text-muted-foreground rounded-2xl bg-card border border-border">
                    Henüz tanımlı belge bulunmuyor.
                  </div>
                )}
                {docs.map((d) => {
                  const up = d.upload;
                  const st = up ? STATUS_MAP[up.status] : null;
                  const StIcon = st?.icon;
                  const needsAction = !up || up.status === 'rejected' || up.status === 're_requested';
                  return (
                    <div key={d.id} className="p-5 rounded-2xl bg-card border border-border" data-testid="document-item">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{d.name}</span>
                            {d.is_required && <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-destructive/10 text-destructive">Zorunlu</span>}
                          </div>
                          {d.description && <p className="text-xs text-muted-foreground mt-0.5">{d.description}</p>}
                          {st && (
                            <span className={`inline-flex items-center gap-1 mt-2 text-xs font-medium px-2 py-0.5 rounded-full ${st.cls}`}>
                              {StIcon && <StIcon className="w-3 h-3" />} {st.label}
                            </span>
                          )}
                          {up?.admin_note && (up.status === 'rejected' || up.status === 're_requested') && (
                            <p className="text-xs text-destructive mt-1.5">Not: {up.admin_note}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {up?.file_path && (
                            <a href={getFileUrl(up.file_path)} target="_blank" rel="noreferrer" className="text-xs text-[hsl(var(--gold))] hover:underline">Görüntüle</a>
                          )}
                          <label className="cursor-pointer">
                            <span className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${needsAction ? 'bg-[hsl(var(--gold))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--gold)/0.92)]' : 'border border-border hover:bg-secondary'}`}>
                              {uploadingDoc === d.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                              {up ? 'Yeniden Yükle' : 'Yükle'}
                            </span>
                            <input type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => handleDocUpload(d.id, e.target.files[0])} data-testid={`doc-upload-${d.id}`} />
                          </label>
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          )}
          {/* Payments tab */}
          {paymentsEnabled && activeTab === 'payments' && (
            <PaymentsPanel />
          )}

          {/* Wet-signature documents tab */}
          {wetSigEnabled && activeTab === 'signature' && (() => {
            const sub = sig.submission;
            const done = sub?.status === 'completed';
            const shipped = sub?.status === 'shipped';
            return (
              <div className="space-y-4" data-testid="signature-list">
                {sig.mailing_address && (
                  <div className="p-4 rounded-xl bg-secondary/50 border border-border text-sm">
                    <span className="font-medium">Kargo Adresi:</span> {sig.mailing_address}
                    <p className="text-xs text-muted-foreground mt-1">Belgeleri indirip imzalayın ve bu adrese kargolayın. Ardından tek seferde "Kargoladım" butonuna basın.</p>
                  </div>
                )}

                {/* Document list (download only) */}
                {sig.documents.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground text-sm">Şu an indirilebilir belge yok.</div>
                ) : (
                  <div className="space-y-3">
                    {sig.documents.map(d => (
                      <div key={d.id} className="p-4 rounded-xl bg-card border border-border flex flex-wrap items-center gap-3" data-testid="signature-item">
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm flex items-center gap-2 flex-wrap">
                            {d.title}
                            {(d.is_required === true || d.is_required === 'true' || d.is_required === 'True') &&
                              <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-destructive/10 text-destructive">Zorunlu</span>}
                          </div>
                          {d.description && <div className="text-xs text-muted-foreground">{d.description}</div>}
                        </div>
                        <a href={getFileUrl(d.file_path)} target="_blank" rel="noreferrer"
                          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-border text-sm hover:bg-secondary shrink-0" data-testid={`sig-download-${d.id}`}>
                          <Download className="w-4 h-4" /> İndir
                        </a>
                      </div>
                    ))}
                  </div>
                )}

                {/* Single shipping section */}
                {sig.documents.length > 0 && (
                  <div className="p-4 rounded-xl bg-card border border-border" data-testid="signature-ship-box">
                    {done ? (
                      <div className="text-sm text-green-600 dark:text-green-400 font-medium flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> Belgeleriniz teslim alındı — süreç tamamlandı ✓</div>
                    ) : (
                      <>
                        {shipped && <div className="text-sm text-amber-600 dark:text-amber-400 font-medium mb-3 flex items-center gap-2"><Clock className="w-4 h-4" /> Kargolandı — teslim bekleniyor</div>}
                        <p className="text-sm font-medium mb-3">Tüm belgeleri kargoladıysanız kargo bilgilerinizi girin:</p>
                        <div className="flex flex-wrap items-center gap-2">
                          <input value={shipForm.shipping_company} onChange={e => setShipForm(f => ({ ...f, shipping_company: e.target.value }))}
                            placeholder="Kargo firması (ör. Yurtiçi, MNG) *" className={`${inputCls} flex-1 min-w-[180px]`} data-testid="sig-company" />
                          <input value={shipForm.tracking_note} onChange={e => setShipForm(f => ({ ...f, tracking_note: e.target.value }))}
                            placeholder="Kargo takip no (opsiyonel)" className={`${inputCls} flex-1 min-w-[180px]`} data-testid="sig-tracking" />
                          <button onClick={shipSig}
                            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[hsl(var(--gold))] text-[hsl(var(--accent-foreground))] text-sm font-medium hover:bg-[hsl(var(--gold)/0.92)]" data-testid="sig-ship">
                            {shipped ? 'Kargo Bilgisini Güncelle' : 'Kargoladım'}
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      )}

      {/* Wet-signature incomplete warning popup */}
      {showSigWarning && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" data-testid="sig-warning-popup">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowSigWarning(false)} />
          <div className="relative w-full max-w-md rounded-2xl bg-card border border-border shadow-[var(--shadow-elev-1)] p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-amber-500/15 flex items-center justify-center shrink-0">
                <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
              </div>
              <h3 className="font-heading text-lg font-semibold">Resmi Kaydınız Tamamlanmadı</h3>
            </div>
            <p className="text-sm text-muted-foreground whitespace-pre-line mb-5">{sig.warning?.message}</p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowSigWarning(false)} className="px-4 py-2 rounded-lg border border-input text-sm hover:bg-secondary" data-testid="sig-warning-close">Kapat</button>
              <button onClick={() => { setShowSigWarning(false); setActiveTab('signature'); }}
                className="px-4 py-2 rounded-lg bg-[hsl(var(--gold))] text-[hsl(var(--accent-foreground))] text-sm font-medium hover:bg-[hsl(var(--gold)/0.92)]" data-testid="sig-warning-goto">
                Islak İmza Belgeleri
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
